import React, { useState } from 'react';

import { faUpload } from '@fortawesome/pro-solid-svg-icons';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';

import { post } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';
import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';

import type { ReportData } from './types';

interface Props {
  reportsPath: string;
}

// Starts a report from a PDF someone already has, for a county the finder couldn't help with.
export default function UploadForm({ reportsPath }: Props) {
  const [countyFips, setCountyFips] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [countyName, setCountyName] = useState<string>('');
  const [fiscalYear, setFiscalYear] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!file) return;

    const body = new FormData();
    body.append('acfr_report[county_fips]', countyFips);
    body.append('acfr_report[state]', state.toUpperCase());
    body.append('acfr_report[county_name]', countyName);
    body.append('acfr_report[fiscal_year]', fiscalYear);
    body.append('pdf', file);

    setSaving(true);
    try {
      const result: ReportData = await post(reportsPath, body);
      window.location.assign(result.report.path);
    } catch (error) {
      setErrors(requestErrors(error));
      setSaving(false);
    }
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title>Upload an ACFR</Card.Title>
      </Card.Header>
      <Card.Body>
        <form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <Errors errors={errors} />
          <Field>
            <Field.Label>County FIPS</Field.Label>
            <Input
              pattern="\d{5}"
              placeholder="51013"
              required
              value={countyFips}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCountyFips(event.target.value)}
            />
          </Field>
          <Field>
            <Field.Label>State</Field.Label>
            <Input
              maxLength={2}
              placeholder="VA"
              required
              value={state}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setState(event.target.value)}
            />
          </Field>
          <Field>
            <Field.Label>County name</Field.Label>
            <Input
              placeholder="Arlington County"
              value={countyName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCountyName(event.target.value)}
            />
          </Field>
          <Field>
            <Field.Label>Fiscal year</Field.Label>
            <Input
              placeholder="Read from the PDF if blank"
              type="number"
              value={fiscalYear}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFiscalYear(event.target.value)}
            />
          </Field>
          <Field>
            <Field.Label>PDF</Field.Label>
            <Input
              accept="application/pdf"
              required
              type="file"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFile(event.target.files?.[0] ?? null)}
            />
          </Field>
          <div>
            <Button disabled={saving || !file} icon={faUpload} label="Upload and extract" type="submit" />
          </div>
        </form>
      </Card.Body>
    </Card>
  );
}
